import React from "react";
import { Form, Nav, Button, Spinner, Alert } from "react-bootstrap";
import ExpertResultRow from "./ExpertResultRow";

// The "any" default is labelled "Login recency" so the closed dropdown
// reads as a self-describing filter affordance — no separate label
// alongside it. The other options use a "Within" prefix so the selected
// state reads naturally ("Within 1 month").
const LOGIN_RECENCY_OPTIONS = [
  { value: "any", label: "Login recency" },
  { value: "1month", label: "Within 1 month" },
  { value: "3months", label: "Within 3 months" },
  { value: "6months", label: "Within 6 months" },
];

// Hoisted so they aren't reallocated on every render.
const CLOSE_BUTTON_STYLE = { width: "28px", height: "28px" };
const CLOSE_ICON_STYLE = { fontSize: "0.8rem" };

// Pure-presentational search form used by ExpertPicker's advanced view.
//   - Top row: tab buttons (By text / By name) on the left; the compact
//     login-recency select and a circular close button on the right.
//   - Bordered content area: per-tab input + optional distance slider.
//   - Edge-to-edge results section below.
// All state lives in the parent ExpertPicker; this component is dumb.
export default function ExpertPickerForm({
  searchMode,
  setSearchMode,
  query,
  setQuery,
  filters,
  setFilters,
  loading,
  error,
  retryAdvised,
  retrySearch,
  visibleResults,
  addExpert,
  atMax,
  onClose,
}) {
  const relaxLoginRecency = () => setFilters((f) => ({ ...f, loginRecency: "any" }));

  return (
    <div>
      {/* Top row: tabs (left) + login recency + close X (right). `flex-nowrap`
          on the Nav forces tabs to stay on a single line — Bootstrap's `.nav`
          class defaults to flex-wrap which would otherwise stack the tabs in
          a tight column. */}
      <div className="d-flex align-items-end justify-content-between gap-2">
        <Nav
          variant="tabs"
          activeKey={searchMode}
          onSelect={(k) => k && setSearchMode(k)}
          className="flex-nowrap small"
        >
          <Nav.Item>
            <Nav.Link eventKey="semantic">By text</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="by_name">By name</Nav.Link>
          </Nav.Item>
        </Nav>
        <div className="d-flex align-items-center gap-2 mb-1">
          <Form.Select
            id="expert-picker-login-recency"
            aria-label="Login recency filter"
            value={filters.loginRecency}
            onChange={(e) => setFilters((f) => ({ ...f, loginRecency: e.target.value }))}
            // Tighter than form-select-sm: smaller font, less padding, no
            // explicit width so the field hugs its content.
            style={{
              width: "auto",
              fontSize: "0.75rem",
              padding: "0.125rem 1.5rem 0.125rem 0.5rem",
              backgroundPosition: "right 0.4rem center",
              backgroundSize: "12px 9px",
            }}
            className="form-select"
          >
            {LOGIN_RECENCY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
          {onClose && (
            <Button
              variant="dark"
              size="sm"
              className="rounded-circle d-flex align-items-center justify-content-center p-0"
              style={CLOSE_BUTTON_STYLE}
              onClick={onClose}
              aria-label="Close advanced search"
              title="Close advanced search"
            >
              <i className="fa-solid fa-xmark text-white" aria-hidden="true" style={CLOSE_ICON_STYLE} />
            </Button>
          )}
        </div>
      </div>

      {/* Bordered content area. `border-top-0` because the tabs above already
          carry the top edge; the active tab merges with this area's top.
          The shell carries no padding — the search-controls section adds its
          own, and the results section runs edge-to-edge. */}
      <div className="border border-top-0 rounded-bottom bg-white">
        <div className="p-3">
          {searchMode === "semantic" && (
            <>
              <Form.Control
                as="textarea"
                rows={5}
                placeholder="Describe the kind of expert you want (e.g. AI regulation in healthcare)"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="mb-2"
              />
              <Form.Group>
                <Form.Label className="small mb-0">Distance &lt; {filters.withinDistance.toFixed(2)}</Form.Label>
                <Form.Range
                  min={0.3}
                  max={1.0}
                  step={0.05}
                  value={filters.withinDistance}
                  onChange={(e) => setFilters((f) => ({ ...f, withinDistance: parseFloat(e.target.value) }))}
                />
              </Form.Group>
            </>
          )}

          {searchMode === "by_name" && (
            <Form.Control
              type="text"
              placeholder="Search expert by name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
        </div>

        {/* Results section. Edge-to-edge: each row stretches to the picker's
            outer border, separated by the row's own border-bottom. */}
        {(loading || error || query.trim().length > 0) && (
          <div className="border-top" style={{ maxHeight: "320px", overflowY: "auto" }}>
            {loading && (
              <div className="text-center text-muted py-3">
                <Spinner size="sm" animation="border" /> Searching…
              </div>
            )}

            {error && !loading && (
              <Alert variant="danger" className="py-2 px-3 small mb-0 rounded-0 border-0">
                {error}
                {retryAdvised && (
                  <>
                    {" "}
                    <Button size="sm" variant="outline-danger" onClick={retrySearch} className="ms-2">
                      Retry
                    </Button>
                  </>
                )}
                {searchMode === "semantic" && (
                  <>
                    {" "}
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setSearchMode("by_name")}
                      className="ms-2"
                    >
                      Try by-name search
                    </Button>
                  </>
                )}
              </Alert>
            )}

            {!loading && !error && visibleResults.length === 0 && query.trim().length > 0 && (
              <div className="text-muted small px-3 py-3">
                No matches.{" "}
                {filters.loginRecency !== "any" && (
                  <Button size="sm" variant="link" className="p-0 align-baseline" onClick={relaxLoginRecency}>
                    Try removing the login recency filter
                  </Button>
                )}
              </div>
            )}

            {visibleResults.length > 0 &&
              visibleResults.map((expert) => (
                <ExpertResultRow key={expert.id} expert={expert} onAdd={addExpert} atMax={atMax} />
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
